import { Link } from '@inertiajs/react';

import Container from '@/components/public/ui/Container';
import Icon from '@/components/public/ui/Icon';
import { FooterLinks } from '@/data/publicNavigation';
import { trackUmamiEvent } from '@/lib/analytics';
import { parishName, parishLogoSmall, useSite } from '@/hooks/useSite';

// Dark institutional footer (`section-ink`) with a gov rule on top.
export default function Footer() {
  const site = useSite();
  const name = parishName(site);
  const hq = site.locations.find((l) => l.headquarters) ?? site.locations[0];
  const address = hq?.address ?? null;
  const phone = hq?.phone ?? null;
  const email = site.contactEmail ?? hq?.email ?? null;
  const subtitle = ['Junta de Freguesia', site.district && `Distrito de ${site.district}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <footer className="relative z-10 mt-auto">
      <div className="gov-rule" />
      <div className="section-ink">
        <Container className="pt-12 pb-8">
          {/* Brand row */}
          <div className="mb-10 flex items-center gap-4 border-b border-white/10 pb-8">
            <img
              src={parishLogoSmall(site)}
              alt={`Brasão de ${name}`}
              width="72"
              height="48"
              className="h-12 w-auto flex-shrink-0"
            />
            <div className="leading-tight">
              <p className="font-headline text-[17px] font-semibold text-[#fbf8f1]">{name}</p>
              <p className="text-[13px] text-[#a89e8b]">{subtitle}</p>
            </div>
          </div>

          {/* Columns */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="max-w-xs text-sm text-[#c2b9a6]">
                Trabalhamos diariamente para o desenvolvimento e bem-estar de toda a nossa
                comunidade, preservando a nossa identidade e património.
              </p>
            </div>

            <div>
              <h4 className="text-gold-leaf mb-4 text-xs font-bold tracking-[0.12em] uppercase">
                Institucional
              </h4>
              <div className="flex flex-col gap-3">
                {FooterLinks.institucional.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    onClick={() =>
                      trackUmamiEvent('footer_link_click', {
                        section: 'institucional',
                        label: link.label,
                        target: link.href,
                      })
                    }
                    className="text-sm text-[#cfc7b6] transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-gold-leaf mb-4 text-xs font-bold tracking-[0.12em] uppercase">
                Explorar
              </h4>
              <div className="flex flex-col gap-3">
                {FooterLinks.explorar.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    onClick={() =>
                      trackUmamiEvent('footer_link_click', {
                        section: 'explorar',
                        label: link.label,
                        target: link.href,
                      })
                    }
                    className="text-sm text-[#cfc7b6] transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-gold-leaf mb-4 text-xs font-bold tracking-[0.12em] uppercase">
                Contacto
              </h4>
              <div className="flex flex-col gap-4">
                {address && (
                  <div className="flex items-start gap-3">
                    <Icon name="location_on" size="sm" className="mt-0.5 text-[#a89e8b]" />
                    <span className="text-sm text-[#cfc7b6]">{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-start gap-3">
                    <Icon name="phone" size="sm" className="mt-0.5 text-[#a89e8b]" />
                    <a
                      href={`tel:${phone}`}
                      onClick={() => trackUmamiEvent('phone_click', { source: 'footer' })}
                      className="text-sm text-[#cfc7b6] transition-colors hover:text-white"
                    >
                      {phone}
                    </a>
                  </div>
                )}
                {email && (
                  <div className="flex items-start gap-3">
                    <Icon name="email" size="sm" className="mt-0.5 text-[#a89e8b]" />
                    <a
                      href={`mailto:${email}`}
                      onClick={() =>
                        trackUmamiEvent('email_click', {
                          source: 'footer',
                          target: 'contact_email',
                        })
                      }
                      className="text-sm text-[#cfc7b6] transition-colors hover:text-white"
                    >
                      {email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>

        {/* Bottom bar */}
        <div className="border-t border-white/10">
          <Container className="flex flex-col items-center justify-between gap-4 py-4 md:flex-row">
            <p className="text-center text-xs text-[#a89e8b] md:text-left">
              &copy; {new Date().getFullYear()} {name}. Todos os direitos reservados.
            </p>
            <div className="flex gap-6">
              <Link
                href="/privacy"
                onClick={() =>
                  trackUmamiEvent('footer_link_click', {
                    section: 'legal',
                    label: 'Privacidade',
                    target: '/privacy',
                  })
                }
                className="text-xs text-[#a89e8b] transition-colors hover:text-white"
              >
                Privacidade
              </Link>
              <Link
                href="/accessibility"
                onClick={() =>
                  trackUmamiEvent('footer_link_click', {
                    section: 'legal',
                    label: 'Acessibilidade',
                    target: '/accessibility',
                  })
                }
                className="text-xs text-[#a89e8b] transition-colors hover:text-white"
              >
                Acessibilidade
              </Link>
            </div>
          </Container>
        </div>
      </div>
    </footer>
  );
}
